import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar, Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem,
  Paper, Select, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteOutline, Refresh, Send, Settings, Link as LinkIcon, PictureAsPdf } from '@material-ui/icons';
import api from '../../../services/api';

const PAX_TYPES = [
  { id: 1, label: 'ADT' },
  { id: 2, label: 'CHD' },
  { id: 3, label: 'INF' },
  { id: 5, label: 'SEN' }
];

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  toolbar: { marginBottom: theme.spacing(2) },
  card: { marginBottom: theme.spacing(2) },
  tableWrap: { overflowX: 'auto' },
  statusOk: { color: '#2e7d32', fontWeight: 600 },
  statusWarn: { color: '#ed6c02', fontWeight: 600 },
  statusError: { color: '#d32f2f', fontWeight: 600 },
  monospace: { fontFamily: 'monospace' }
}));

function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function errorMessage(error) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Erro inesperado.';
}

function OrbitIntegration() {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [configs, setConfigs] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [dateIni, setDateIni] = useState(todayISO());
  const [dateFim, setDateFim] = useState(todayISO());
  const [configDialog, setConfigDialog] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(0);
  const [configForm, setConfigForm] = useState({
    descricao: '', enterprise: '', baseurl: 'https://orbit-parceiros.gramadoparks.com/api/partner/v1',
    publickey: '', secretkey: '', webhooksecret: '', ambiente: 'SANDBOX', ativo: true
  });
  const [mapForm, setMapForm] = useState({ 1: '', 2: '', 3: '', 5: '' });

  const selectedConfigObj = useMemo(
    () => configs.find(c => String(c.id) === String(selectedConfig)),
    [configs, selectedConfig]
  );

  async function loadConfigs() {
    const response = await api.get('/orbit/config');
    const data = response.data?.data || [];
    setConfigs(data);
    if (!selectedConfig && data.length) setSelectedConfig(String(data[0].id));
  }

  async function loadServices() {
    const response = await api.get('/servico/active/true?limit=1000');
    const data = response.data?.data || [];
    setServices(data);
  }

  async function loadMappings(configId = selectedConfig, serviceId = selectedService) {
    if (!configId) return;
    const params = { idconfig: configId };
    if (serviceId) params.codservico = serviceId;
    const response = await api.get('/orbit/mappings', { params });
    const data = response.data?.data || [];
    setMappings(data);
    const next = { 1: '', 2: '', 3: '', 5: '' };
    data.forEach(m => { if (String(m.codservico) === String(serviceId)) next[m.tipopax] = String(m.productid); });
    setMapForm(next);
  }

  async function loadProducts(configId = selectedConfig) {
    if (!configId) return;
    setLoading(true);
    try {
      const response = await api.get(`/orbit/config/${configId}/products`, { params: { type: 'ingresso' } });
      setProducts(response.data?.data || []);
      setMessage('Catálogo Orbit atualizado.');
    } catch (error) {
      setProducts([]);
      setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    Promise.all([loadConfigs(), loadServices()]).catch(error => setMessage(errorMessage(error)));
  }, []);

  useEffect(() => {
    if (selectedConfig) loadMappings(selectedConfig, selectedService).catch(error => setMessage(errorMessage(error)));
  }, [selectedConfig, selectedService]);

  function newConfig() {
    setEditingConfigId(0);
    setConfigForm({
      descricao: '', enterprise: '', baseurl: 'https://orbit-parceiros.gramadoparks.com/api/partner/v1',
      publickey: '', secretkey: '', webhooksecret: '', ambiente: 'SANDBOX', ativo: true
    });
    setConfigDialog(true);
  }

  function editConfig(cfg) {
    setEditingConfigId(cfg.id);
    setConfigForm({
      descricao: cfg.descricao || '', enterprise: cfg.enterprise || '', baseurl: cfg.baseurl || '',
      publickey: cfg.publickey || '', secretkey: '********', webhooksecret: cfg.temwebhooksecret === 'S' ? '********' : '',
      ambiente: cfg.ambiente || 'SANDBOX', ativo: cfg.ativo !== 'N'
    });
    setConfigDialog(true);
  }

  async function saveConfig() {
    setLoading(true);
    try {
      const payload = { ...configForm, ativo: configForm.ativo ? 'S' : 'N', codempresa: Number(sessionStorage.getItem('codEmpresa') || 0) };
      if (editingConfigId) await api.put(`/orbit/config/${editingConfigId}`, payload);
      else await api.post('/orbit/config', payload);
      setConfigDialog(false);
      await loadConfigs();
      setMessage('Configuração salva com sucesso.');
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function testConfig(id) {
    setLoading(true);
    try {
      const response = await api.post(`/orbit/config/${id}/test`);
      setMessage(`${response.data.message} ${response.data.products?.length || 0} produto(s) retornado(s).`);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally { setLoading(false); }
  }

  async function deleteConfig(id) {
    if (!window.confirm('Excluir esta configuração Orbit?')) return;
    try {
      await api.delete(`/orbit/config/${id}`);
      if (String(selectedConfig) === String(id)) setSelectedConfig('');
      await loadConfigs();
      setMessage('Configuração excluída.');
    } catch (error) { setMessage(errorMessage(error)); }
  }

  async function saveMappings() {
    if (!selectedConfig || !selectedService) {
      setMessage('Selecione a configuração e o serviço Sisare.');
      return;
    }
    setLoading(true);
    try {
      for (const pax of PAX_TYPES) {
        const productId = Number(mapForm[pax.id] || 0);
        const existing = mappings.find(m => String(m.codservico) === String(selectedService) && Number(m.tipopax) === pax.id);
        if (productId) {
          const payload = { idconfigorbit: Number(selectedConfig), codservico: Number(selectedService), tipopax: pax.id, productid: productId, ativo: 'S' };
          if (existing) await api.put(`/orbit/mappings/${existing.id}`, payload);
          else await api.post('/orbit/mappings', payload);
        } else if (existing) {
          await api.delete(`/orbit/mappings/${existing.id}`);
        }
      }
      await loadMappings();
      setMessage('Vínculos de produtos salvos.');
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function searchReservations() {
    if (!selectedConfig || !selectedService) {
      setMessage('Selecione configuração e serviço.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/orbit/reservations', {
        params: { idconfig: selectedConfig, codservico: selectedService, dataini: dateIni, datafim: dateFim }
      });
      setReservations(response.data?.data || []);
      setMessage(`${response.data?.data?.length || 0} reserva(s) encontrada(s).`);
    } catch (error) {
      setReservations([]);
      setMessage(errorMessage(error));
    } finally { setLoading(false); }
  }

  async function sendReservation(row) {
    if (!row.validacao?.ok) {
      setMessage('Esta reserva possui pendências de cadastro ou de mapeamento.');
      return;
    }
    if (!window.confirm(`Enviar OS ${row.nroos} / item ${row.coditemos} para a Orbit?`)) return;
    setLoading(true);
    try {
      await api.post('/orbit/orders', { idconfig: Number(selectedConfig), coditemos: row.coditemos, codfamilia: row.codfamilia || 0 });
      setMessage('Venda enviada à Orbit.');
      await searchReservations();
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function refreshOrder(row) {
    const localId = row.integracao?.id;
    if (!localId) return;
    setLoading(true);
    try {
      await api.post(`/orbit/orders/${localId}/refresh`);
      setMessage('Situação Orbit atualizada.');
      await searchReservations();
    } catch (error) { setMessage(errorMessage(error)); }
    finally { setLoading(false); }
  }

  async function openVoucher(row) {
    try {
      const response = await api.get(`/orbit/orders/${row.integracao.id}/voucher`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) { setMessage(errorMessage(error)); }
  }

  function validationText(row) {
    const v = row.validacao || {};
    const parts = [];
    if (v.mapeamentosfaltantes?.length) parts.push(`sem produto: ${v.mapeamentosfaltantes.join(', ')}`);
    if (v.paxdadosfaltantes?.length) parts.push(`${v.paxdadosfaltantes.length} pax sem documento/nascimento`);
    if (v.titularfonefaltante) parts.push('titular sem telefone');
    if (v.titularemailfaltante) parts.push('titular sem e-mail');
    return parts.join('; ') || 'OK';
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>Integração Gramado Parks / Orbit</Typography>
      <Typography variant="body2" color="textSecondary" className={classes.toolbar}>
        Mantém o serviço único do Sisare e converte ADT/CHD/INF/SEN nos produtos correspondentes da Orbit.
      </Typography>

      {message && <Paper style={{ padding: 12, marginBottom: 16 }}><Typography>{message}</Typography></Paper>}
      {loading && <Box display="flex" alignItems="center" mb={2}><CircularProgress size={22} /><Box ml={1}>Processando...</Box></Box>}

      <AppBar position="static" color="default">
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab icon={<Send />} label="Reservas / Vendas" />
          <Tab icon={<LinkIcon />} label="Produtos por tipo de pax" />
          <Tab icon={<Settings />} label="Configurações" />
        </Tabs>
      </AppBar>

      {tab === 0 && <Box mt={2}>
        <Card className={classes.card}><CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Integração / Parque</InputLabel><Select value={selectedConfig} onChange={e => setSelectedConfig(e.target.value)}>{configs.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Serviço Sisare</InputLabel><Select value={selectedService} onChange={e => setSelectedService(e.target.value)}>{services.map(s => <MenuItem key={s.codservico} value={String(s.codservico)}>{s.codservico} - {s.descservico}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth label="Data inicial" type="date" value={dateIni} onChange={e => setDateIni(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth label="Data final" type="date" value={dateFim} onChange={e => setDateFim(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} md={2}><Button fullWidth variant="contained" color="primary" onClick={searchReservations}>Pesquisar</Button></Grid>
          </Grid>
        </CardContent></Card>

        <Paper className={classes.tableWrap}>
          <Table size="small">
            <TableHead><TableRow><TableCell>OS</TableCell><TableCell>Item</TableCell><TableCell>Data</TableCell><TableCell>Titular</TableCell><TableCell>ADT</TableCell><TableCell>CHD</TableCell><TableCell>INF</TableCell><TableCell>SEN</TableCell><TableCell>Validação</TableCell><TableCell>Orbit</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead>
            <TableBody>{reservations.map(row => {
              const integ = row.integracao;
              const issued = integ?.emissionstatus === 'issued';
              return <TableRow key={`${row.coditemos}-${row.codfamilia}`}>
                <TableCell>{row.nroos}</TableCell><TableCell>{row.coditemos}</TableCell><TableCell>{row.dataservico}</TableCell>
                <TableCell>{row.titular?.nome}</TableCell><TableCell>{row.qtdeadt}</TableCell><TableCell>{row.qtdechd}</TableCell><TableCell>{row.qtdeinf}</TableCell><TableCell>{row.qtdesen}</TableCell>
                <TableCell className={row.validacao?.ok ? classes.statusOk : classes.statusError}>{validationText(row)}</TableCell>
                <TableCell><div>{integ?.orbitordercode || 'Não enviado'}</div><small className={integ?.errorcode ? classes.statusError : (issued ? classes.statusOk : classes.statusWarn)}>{integ?.errorcode ? `${integ.errorcode}: ${integ.errormessage || ''}` : (integ ? `${integ.status || ''} / ${integ.emissionstatus || ''}` : '')}</small></TableCell>
                <TableCell align="right">
                  {!integ?.orbitorderid && <IconButton title="Enviar" onClick={() => sendReservation(row)} disabled={!row.validacao?.ok}><Send /></IconButton>}
                  {integ?.orbitorderid && <IconButton title="Atualizar status" onClick={() => refreshOrder(row)}><Refresh /></IconButton>}
                  {integ?.orbitorderid && (integ.status === 'paid' || issued) && <IconButton title="Voucher" onClick={() => openVoucher(row)}><PictureAsPdf /></IconButton>}
                </TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </Paper>
      </Box>}

      {tab === 1 && <Box mt={2}>
        <Card className={classes.card}><CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Integração / Parque</InputLabel><Select value={selectedConfig} onChange={e => { setSelectedConfig(e.target.value); setProducts([]); }}>{configs.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={5}><FormControl fullWidth><InputLabel>Serviço Sisare</InputLabel><Select value={selectedService} onChange={e => setSelectedService(e.target.value)}>{services.map(s => <MenuItem key={s.codservico} value={String(s.codservico)}>{s.codservico} - {s.descservico}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} md={3}><Button variant="outlined" color="primary" fullWidth onClick={() => loadProducts()}>Carregar catálogo Orbit</Button></Grid>
          </Grid>
          <Box mt={3}><Grid container spacing={2}>{PAX_TYPES.map(p => <Grid item xs={12} md={3} key={p.id}><FormControl fullWidth><InputLabel>{p.label}</InputLabel><Select value={mapForm[p.id] || ''} onChange={e => setMapForm({ ...mapForm, [p.id]: e.target.value })}><MenuItem value=""><em>Não utilizado</em></MenuItem>{products.map(prod => <MenuItem key={prod.id} value={String(prod.id)}>{prod.id} - {prod.short_name || prod.name}</MenuItem>)}</Select></FormControl></Grid>)}</Grid></Box>
          <Box mt={3}><Button variant="contained" color="primary" onClick={saveMappings}>Salvar vínculos</Button></Box>
        </CardContent></Card>
        <Typography variant="body2" color="textSecondary">O item da OS continua sendo um único CODSERVICO. Estes vínculos são usados somente para montar os items[] do pedido Orbit.</Typography>
      </Box>}

      {tab === 2 && <Box mt={2}>
        <Box mb={2}><Button variant="contained" color="primary" onClick={newConfig}>Nova configuração</Button></Box>
        <Paper className={classes.tableWrap}><Table size="small"><TableHead><TableRow><TableCell>Descrição</TableCell><TableCell>Parque</TableCell><TableCell>Ambiente</TableCell><TableCell>URL</TableCell><TableCell>PK</TableCell><TableCell>SK</TableCell><TableCell>Webhook</TableCell><TableCell>Ativo</TableCell><TableCell align="right">Ações</TableCell></TableRow></TableHead><TableBody>{configs.map(c => <TableRow key={c.id}><TableCell>{c.descricao}</TableCell><TableCell>{c.enterprise}</TableCell><TableCell>{c.ambiente}</TableCell><TableCell className={classes.monospace}>{c.baseurl}</TableCell><TableCell className={classes.monospace}>{c.publickey}</TableCell><TableCell>{c.temsecret === 'S' ? 'Configurado' : 'Não'}</TableCell><TableCell>{c.temwebhooksecret === 'S' ? 'Configurado' : 'Não'}</TableCell><TableCell>{c.ativo === 'N' ? 'Não' : 'Sim'}</TableCell><TableCell align="right"><Button size="small" onClick={() => testConfig(c.id)}>Testar</Button><Button size="small" onClick={() => editConfig(c)}>Editar</Button><IconButton size="small" onClick={() => deleteConfig(c.id)}><DeleteOutline /></IconButton></TableCell></TableRow>)}</TableBody></Table></Paper>
        {selectedConfigObj && <Box mt={2}><Typography variant="body2"><strong>URL para cadastrar no painel Orbit:</strong> {`${window.location.origin.replace(/\/$/, '')}/api/orbit/webhook/${selectedConfigObj.id}`} (ajuste o prefixo conforme a publicação da sua API).</Typography></Box>}
      </Box>}

      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfigId ? 'Editar configuração Orbit' : 'Nova configuração Orbit'}</DialogTitle>
        <DialogContent><Grid container spacing={2}>
          <Grid item xs={12} md={6}><TextField fullWidth label="Descrição" value={configForm.descricao} onChange={e => setConfigForm({ ...configForm, descricao: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Parque / Enterprise" value={configForm.enterprise} onChange={e => setConfigForm({ ...configForm, enterprise: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Base URL" value={configForm.baseurl} onChange={e => setConfigForm({ ...configForm, baseurl: e.target.value })} helperText="Ex.: https://orbit-parceiros.gramadoparks.com/api/partner/v1" /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth label="Public Key (pk_)" value={configForm.publickey} onChange={e => setConfigForm({ ...configForm, publickey: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth type="password" label="Secret Key (sk_)" value={configForm.secretkey} onChange={e => setConfigForm({ ...configForm, secretkey: e.target.value })} /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth type="password" label="Segredo do Webhook" value={configForm.webhooksecret} onChange={e => setConfigForm({ ...configForm, webhooksecret: e.target.value })} /></Grid>
          <Grid item xs={12} md={3}><FormControl fullWidth><InputLabel>Ambiente</InputLabel><Select value={configForm.ambiente} onChange={e => setConfigForm({ ...configForm, ambiente: e.target.value })}><MenuItem value="SANDBOX">Sandbox</MenuItem><MenuItem value="PRODUCAO">Produção</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12} md={3}><FormControlLabel control={<Checkbox checked={configForm.ativo} onChange={e => setConfigForm({ ...configForm, ativo: e.target.checked })} color="primary" />} label="Ativo" /></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setConfigDialog(false)}>Cancelar</Button><Button color="primary" variant="contained" onClick={saveConfig}>Salvar</Button></DialogActions>
      </Dialog>
    </div>
  );
}

export default OrbitIntegration;
